import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { installationTypeService } from '../services/api';

export default function InstallationTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    order: 0,
    is_active: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const response = await installationTypeService.getAllTypes();
      setTypes(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditMode(true);
      setSelectedType(type);
      setFormData({
        code: type.code || '',
        name_ar: type.name_ar || '',
        name_en: type.name_en || '',
        order: type.order ?? 0,
        is_active: type.is_active !== false,
      });
    } else {
      setEditMode(false);
      setSelectedType(null);
      setFormData({
        code: '',
        name_ar: '',
        name_en: '',
        order: 0,
        is_active: true,
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.code.trim() || !formData.name_ar.trim()) {
      setError('Code and Arabic name are required');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(formData.code.trim().toLowerCase())) {
      setError('Code must contain only lowercase letters, numbers, and underscores');
      return;
    }

    try {
      const payload = {
        code: formData.code.trim().toLowerCase(),
        name_ar: formData.name_ar.trim(),
        name_en: formData.name_en?.trim() || '',
        order: Number(formData.order) || 0,
        is_active: formData.is_active,
      };
      if (editMode) {
        await installationTypeService.updateType(selectedType._id, payload);
        setSuccess('Installation type updated successfully');
      } else {
        await installationTypeService.createType(payload);
        setSuccess('Installation type created successfully');
      }
      setDialogOpen(false);
      loadTypes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = (type) => {
    setTypeToDelete(type);
    setDeleteDialogOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    try {
      await installationTypeService.deleteType(typeToDelete._id);
      setSuccess(`"${typeToDelete.name_ar}" deleted successfully`);
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
      loadTypes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { field: 'code', headerName: 'Code', width: 140 },
    { field: 'name_ar', headerName: 'Name (Arabic)', width: 150 },
    { field: 'name_en', headerName: 'Name (English)', width: 150 },
    { field: 'order', headerName: 'Order', width: 80, type: 'number' },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          size='small'
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Button
            variant='outlined'
            size='small'
            onClick={() => handleOpenDialog(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>Installation Types</Typography>
        <Button variant='contained' startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Type
        </Button>
      </Box>

      {error && !dialogOpen && !deleteDialogOpen && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            These types appear in the mobile app and when creating installations. Code is used in the database (e.g. buildings, pole). Deactivating a type hides it from new installations but keeps existing data.
          </Typography>
          <DataGrid
            rows={types}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>{editMode ? 'Edit Installation Type' : 'Add Installation Type'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin='dense'
            label='Code (lowercase, e.g. buildings, pole)'
            fullWidth
            variant='outlined'
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            disabled={editMode}
            helperText={editMode ? 'Code cannot be changed after creation' : 'Letters, numbers, underscores only'}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin='dense'
            label='Name (Arabic)'
            fullWidth
            variant='outlined'
            value={formData.name_ar}
            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin='dense'
            label='Name (English, optional)'
            fullWidth
            variant='outlined'
            value={formData.name_en}
            onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin='dense'
            label='Display order'
            type='number'
            fullWidth
            variant='outlined'
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: e.target.value })}
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label='Active (shown in app)'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant='contained'>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Installation Type</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete &quot;{typeToDelete?.name_ar}&quot; (code: {typeToDelete?.code})?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Existing installations using this type will keep the code but the type will no longer appear in dropdowns.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setTypeToDelete(null);
              setError('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant='contained' color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
